import { useEffect, useState } from "react";
import { X, Tractor } from "lucide-react";
import { api } from "../../api";
import { looksLikeId } from "../../api/client";
import { useUiStore } from "../../stores/uiStore";
import { useNewField } from "../../hooks/useNewField";
import { useJdConnect } from "../../hooks/useJdConnect";
// shared JD id-key tolerance (lib/jdShape) — one source of truth for every JD picker
import { pickId } from "../../lib/jdShape";
import { Modal } from "../../ui/Modal";

interface JdField {
  jd_org_id: string;
  jd_field_id: string;
  name: string;
  org_name?: string;
}
type Phase = "checking" | "unavailable" | "connect" | "ready" | "error";

// Pull a boundary from JD Ops (Front-End.md §A9). Status-gated; fields are fetched across
// ALL of the user's JD orgs so a field in any org shows up (not just orgs[0]).
export function JdPullModal() {
  const close = useUiStore((s) => s.closeNewField);
  const { fromJd } = useNewField();
  const { status, refresh, connect, connecting } = useJdConnect();
  const [phase, setPhase] = useState<Phase>("checking");
  const [fields, setFields] = useState<JdField[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void refresh().catch((e) => {
      setErr((e as Error).message);
      setPhase("error");
    });
  }, [refresh]);

  useEffect(() => {
    if (!status) return;
    if (!status.configured) return setPhase("unavailable");
    if (!status.connected) return setPhase("connect");
    void (async () => {
      setPhase("checking");
      setErr(null);
      try {
        const orgs = (await api.jdOrgs()) as unknown as Record<
          string,
          unknown
        >[];
        const nested = await Promise.all(
          orgs.map(async (o) => {
            const jd_org_id = pickId(o, ["jd_org_id", "id", "org_id"]);
            const org_name = typeof o.name === "string" ? o.name : undefined;
            try {
              const fs = (await api.jdFields(jd_org_id)) as unknown as Record<
                string,
                unknown
              >[];
              return fs.map((f) => ({
                jd_org_id,
                jd_field_id: pickId(f, ["jd_field_id", "id", "field_id"]),
                name:
                  typeof f.name === "string"
                    ? f.name
                    : pickId(f, ["jd_field_id", "id"]),
                org_name,
              }));
            } catch {
              return [] as JdField[];
            }
          }),
        );
        setFields(nested.flat());
        setPhase("ready");
      } catch (e) {
        setErr((e as Error).message);
        setPhase("error");
      }
    })();
  }, [status]);

  async function pick(f: JdField) {
    setBusy(f.jd_field_id);
    try {
      // carry the JD field's NAME into our field (#3) — but only a REAL name, not an id-like
      // fallback (JD payloads often carry only GUIDs); an id-like value → omit so the backend/mock
      // synthesizes a readable "JD <id>" instead of naming the field after a GUID.
      const jdName = f.name && !looksLikeId(f.name) ? f.name : undefined;
      await fromJd(f.jd_org_id, f.jd_field_id, jdName);
      close();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(null);
    }
  }

  const multiOrg = new Set(fields.map((f) => f.jd_org_id)).size > 1;

  return (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
        onClick={close}
      >
        <div
          className="w-full max-w-sm rounded-lg bg-surface text-fg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">
              Pull boundary from John Deere Ops
            </h2>
            <button
              onClick={close}
              className="rounded p-1 hover:bg-hover"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-4">
            {(phase === "checking" || connecting) && (
              <p className="text-xs text-muted">
                {connecting
                  ? "Waiting for John Deere login…"
                  : "Checking your John Deere connection…"}
              </p>
            )}

            {phase === "unavailable" && !connecting && (
              <div className="space-y-2 text-center">
                <Tractor size={28} className="mx-auto text-faint" />
                <p className="text-sm text-muted">
                  John Deere Ops isn't available yet.
                </p>
                <p className="text-[11px] text-faint">
                  The integration isn't switched on in this environment — check
                  back soon.
                </p>
              </div>
            )}

            {phase === "connect" && !connecting && (
              <div className="space-y-3 text-center">
                <Tractor size={28} className="mx-auto text-faint" />
                <p className="text-sm text-muted">
                  You're not connected to John Deere Ops yet.
                </p>
                <button
                  className="rounded bg-accent px-4 py-2 text-sm font-medium text-canvas hover:bg-accent-hover"
                  onClick={connect}
                >
                  Log in to John Deere
                </button>
              </div>
            )}

            {phase === "error" && !connecting && (
              <div className="space-y-2">
                <p className="text-xs text-red-400">{err}</p>
                <button
                  className="text-xs text-fg underline"
                  onClick={() => void refresh()}
                >
                  Try again
                </button>
              </div>
            )}

            {phase === "ready" &&
              !connecting &&
              (fields.length === 0 ? (
                <p className="text-xs text-muted">
                  No fields found in your John Deere Ops account.
                </p>
              ) : (
                <ul className="max-h-72 space-y-1 overflow-auto">
                  {fields.map((f) => (
                    <li key={`${f.jd_org_id}:${f.jd_field_id}`}>
                      <button
                        disabled={!!busy}
                        className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-hover disabled:opacity-50"
                        onClick={() => pick(f)}
                      >
                        <span className="truncate">
                          {f.name}
                          {multiOrg && f.org_name && (
                            <span className="ml-1 text-[10px] text-faint">
                              · {f.org_name}
                            </span>
                          )}
                        </span>
                        <span className="ml-2 shrink-0 text-[11px] text-faint">
                          {busy === f.jd_field_id
                            ? "creating…"
                            : "create field"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ))}
            {err && phase === "ready" && (
              <p className="mt-1 text-[11px] text-red-400">{err}</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
